/**
 * 📱 SOCIAL & TEASER - Snapchat + Teaser Espace Client
 * Snap actif + teaser mystérieux pour les réseaux à venir
 */

import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Clock, ExternalLink, MessageCircle, Lock, Sparkles, Rocket, Wifi } from 'lucide-react';

const SocialContactSection = () => {
  return (
    <section className="relative py-20 px-4">
      {/* Header */}
      <div className="text-center mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20 mb-4"
        >
          <MessageCircle className="w-4 h-4 text-pink-400" />
          <span className="text-pink-400 text-sm font-mono uppercase tracking-wider">Restez Connectés</span>
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-4xl md:text-5xl font-black text-white mb-4"
        >
          Rejoignez la <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400">Communauté</span>
        </motion.h2>
      </div>

      <div className="max-w-5xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8">
          
          {/* Left: Snapchat + Teaser */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            {/* Snapchat - Actif */}
            <motion.a
              href="https://www.snapchat.com/@lapetiteepi62"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 border border-yellow-500/30 hover:scale-[1.02] transition-all"
              whileHover={{ boxShadow: '0 0 30px rgba(255, 252, 0, 0.3)' }}
            >
              <motion.div
                className="w-16 h-16 rounded-xl flex items-center justify-center text-3xl"
                style={{ background: '#FFFC00' }}
                whileHover={{ rotate: [0, -10, 10, 0] }}
              >
                👻
              </motion.div>
              
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-bold text-lg">Snapchat</h3>
                  <span className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 text-xs font-bold">ACTIF</span>
                </div>
                <p className="text-yellow-400 font-semibold">@lapetiteepice62</p>
                <p className="text-gray-400 text-sm">Coulisses, promos flash, daily life du magasin</p>
              </div>

              <ExternalLink className="w-5 h-5 text-gray-500 group-hover:text-yellow-400 transition-colors" />
            </motion.a>

            {/* Teaser Mystérieux - Espace Client */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="p-5 rounded-2xl overflow-hidden relative"
              style={{
                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(139, 92, 246, 0.1))',
                border: '1px solid rgba(6, 182, 212, 0.3)'
              }}
            >
              {/* Scan effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-500/10 to-transparent"
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              />

              <div className="relative">
                <div className="flex items-center gap-4 mb-4">
                  <motion.div
                    className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-cyan-500/30 flex items-center justify-center"
                    animate={{ 
                      boxShadow: ['0 0 15px rgba(6, 182, 212, 0.3)', '0 0 30px rgba(6, 182, 212, 0.5)', '0 0 15px rgba(6, 182, 212, 0.3)']
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Rocket className="w-7 h-7 text-cyan-400" />
                  </motion.div>
                  
                  <div>
                    <h3 className="text-white font-bold text-lg">Bientôt plus de connexions...</h3>
                    <p className="text-cyan-400 text-sm">Votre Espace Client est connecté au vaisseau</p>
                  </div>
                </div>

                <div className="space-y-2 pl-2 border-l-2 border-cyan-500/30">
                  <motion.div 
                    className="flex items-center gap-2 text-gray-400 text-sm"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0 }}
                  >
                    <Wifi className="w-4 h-4 text-cyan-400" />
                    <span>Messagerie directe avec le magasin</span>
                  </motion.div>
                  <motion.div 
                    className="flex items-center gap-2 text-gray-400 text-sm"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
                  >
                    <Lock className="w-4 h-4 text-purple-400" />
                    <span>Offres exclusives réservées aux membres</span>
                  </motion.div>
                  {/* Liens réseaux sociaux actifs */}
                  <div className="flex items-center gap-3 pt-2">
                    <a 
                      href="https://www.instagram.com/la_petite_epicerie.shop/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-pink-500/30 text-pink-400 text-sm font-medium hover:from-purple-500/30 hover:to-pink-500/30 transition-all"
                    >
                      <span>📸</span> Instagram
                    </a>
                    <a 
                      href="https://www.facebook.com/profile.php?id=61588543479522"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-400 text-sm font-medium hover:bg-blue-500/30 transition-all"
                    >
                      <span>👤</span> Facebook
                    </a>
                  </div>
                </div>

                <motion.a
                  href="/client"
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/10 transition-colors"
                  whileHover={{ scale: 1.02 }}
                >
                  Découvrir l'Espace Client <ExternalLink className="w-4 h-4" />
                </motion.a>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(139, 92, 246, 0.1))',
              border: '1px solid rgba(6, 182, 212, 0.2)'
            }}
          >
            {/* Scan line */}
            <motion.div
              className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"
              animate={{ top: ['0%', '100%', '0%'] }}
              transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
            />

            <div className="p-8">
              {/* Station Header */}
              <div className="text-center mb-8">
                <motion.div
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-3"
                  animate={{ 
                    boxShadow: ['0 0 10px rgba(6, 182, 212, 0.2)', '0 0 20px rgba(6, 182, 212, 0.4)', '0 0 10px rgba(6, 182, 212, 0.2)']
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-cyan-400 text-xs font-mono uppercase tracking-wider">Station Active</span>
                </motion.div>
                <h3 className="text-2xl font-bold text-white">Coordonnées Spatiales</h3>
              </div>

              {/* Info Items */}
              <div className="space-y-6">
                {/* Adresse */}
                <motion.div
                  className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/5"
                  whileHover={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
                >
                  <div className="w-12 h-12 rounded-xl bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-pink-400" />
                  </div>
                  <div>
                    <p className="text-xs text-pink-400/70 font-mono uppercase tracking-wider mb-1">Position</p>
                    <p className="text-white font-semibold">138 Rue Arthur Lamendin</p>
                    <p className="text-gray-400">62980 Vermelles</p>
                    <a 
                      href="https://maps.google.com/?q=138+Rue+Arthur+Lamendin+62980+Vermelles"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-400 text-sm hover:underline inline-flex items-center gap-1 mt-1"
                    >
                      Ouvrir Maps <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </motion.div>

                {/* Téléphone */}
                <motion.a
                  href="tel:0780648976"
                  className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/5 hover:bg-white/8 transition-colors"
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-green-400/70 font-mono uppercase tracking-wider mb-1">Communication</p>
                    <p className="text-white font-semibold text-xl">07 80 64 89 76</p>
                    <p className="text-gray-400 text-sm">Appelez-nous directement</p>
                  </div>
                </motion.a>

                {/* Horaires */}
                <motion.div
                  className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/5"
                  whileHover={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
                >
                  <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-xs text-cyan-400/70 font-mono uppercase tracking-wider mb-1">Heures d'Opération</p>
                    <p className="text-white font-semibold">7j/7 • Midi - Minuit</p>
                    <p className="text-cyan-300 text-sm">🌙 Ramadan: Ven & Sam 21h-4h !</p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Corner HUD */}
            <div className="absolute top-3 left-3 w-4 h-4 border-t-2 border-l-2 border-cyan-500/50" />
            <div className="absolute top-3 right-3 w-4 h-4 border-t-2 border-r-2 border-cyan-500/50" />
            <div className="absolute bottom-3 left-3 w-4 h-4 border-b-2 border-l-2 border-purple-500/50" />
            <div className="absolute bottom-3 right-3 w-4 h-4 border-b-2 border-r-2 border-purple-500/50" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SocialContactSection;
